/* eslint-disable no-console */

// Utils
import { getGlobalLogLevel, getChannelLogLevel } from './loggerUtils';
import { sanitizeLogValue } from './sanitizeLogValue';
import { forwardWarnOrErrorToRollbar } from './loggerRollbarForwarder';

// Constants
import { DEBUG_LEVEL } from '../../../config/configConstants';

// Internal symbol to mark already sanitized structures to avoid double processing.
const SANITIZED_SYMBOL: unique symbol = Symbol('sanitizedLogValue');

interface PossiblySanitized {
    [SANITIZED_SYMBOL]?: boolean;
}

const markSanitized = <T>(value: T): T => {
    if (value && typeof value === 'object') {
        try {
            (value as PossiblySanitized)[SANITIZED_SYMBOL] = true;
        } catch {
            // ignore non-writable objects
        }
    }
    return value;
};

const isAlreadySanitized = (value: unknown): boolean =>
    !!(value && typeof value === 'object' && (value as PossiblySanitized)[SANITIZED_SYMBOL]);

class MilanoteLogger {
    channelName?: string;

    constructor(channelName?: string) {
        this.channelName = channelName;
    }

    createChannel = (subChannelName: string) => {
        return new MilanoteLogger(this.channelName ? `${this.channelName}.${subChannelName}` : subChannelName);
    };

    consoleLog =
        (logLevel: number, logMethod: (...args: unknown[]) => void, autoSanitize: boolean = true) =>
        (...values: unknown[]) => {
            const debugLevel = this.channelName ? getChannelLogLevel(this.channelName) : getGlobalLogLevel();
            if (debugLevel < logLevel) return;

            // Ensure we always have a message placeholder
            if (values.length === 0) values.push('');

            if (this.channelName) values[0] = `[${this.channelName}] ${values[0]}`;

            if (autoSanitize) {
                // Sanitize all arguments except the first (message string) to avoid leaking PII.
                for (let i = 1; i < values.length; i += 1) {
                    const v = values[i];
                    if (!isAlreadySanitized(v)) {
                        values[i] = markSanitized(sanitizeLogValue(v));
                    }
                }
            }

            logMethod.apply(console, values);

            // Forward warn and error to Rollbar
            if (autoSanitize && logLevel <= DEBUG_LEVEL.WARN) {
                forwardWarnOrErrorToRollbar(logLevel, values);
            }
        };

    // Expose debug logging only in development builds to avoid any chance of leaking
    // verbose diagnostic data (even though we disable autoSanitize for message-first usage).
    // In non-development environments this becomes a no-op function.
    debug =
        process.env.NODE_ENV === 'development'
            ? this.consoleLog(DEBUG_LEVEL.FINE, console.log, false)
            : // Accept any args for call-site compatibility; intentionally do nothing.
              () => {};
    log = this.consoleLog(DEBUG_LEVEL.FINE, console.log);
    info = this.consoleLog(DEBUG_LEVEL.INFO, console.info);
    table = this.consoleLog(
        DEBUG_LEVEL.INFO,
        // Wrap to normalize signature for TypeScript
        (...args: unknown[]) => (console.table as unknown as (...a: unknown[]) => void)(...args),
    );
    // warn/error auto-sanitized
    error = this.consoleLog(DEBUG_LEVEL.ERROR, console.error);
    warn = this.consoleLog(DEBUG_LEVEL.WARN, console.warn);
    groupCollapsed = this.consoleLog(DEBUG_LEVEL.OFF, console.groupCollapsed);
    group = this.consoleLog(DEBUG_LEVEL.OFF, console.group);
    groupEnd = this.consoleLog(DEBUG_LEVEL.OFF, console.groupEnd);
    debugGroupCollapsed = this.consoleLog(DEBUG_LEVEL.OFF, console.groupCollapsed);
    debugGroup = this.consoleLog(DEBUG_LEVEL.OFF, console.group);
    debugGroupEnd = this.consoleLog(DEBUG_LEVEL.OFF, console.groupEnd);
}

const globalLogger = new MilanoteLogger();

export default globalLogger;
